import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ThankYouImage from "../../../assets/images/thankyou.png";

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    background: "linear-gradient(135deg, #ffffff 30%, #f0f4ff 100%)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
    padding: theme.spacing(3),
    maxWidth: "500px",
    width: "90%",
    transition: "all 0.3s ease-in-out",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(2),
    },
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  "& .success-icon": {
    fontSize: "4rem",
    color: theme.palette.success.main,
    animation: "pulse 2s infinite",
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "50%": {
      transform: "scale(1.2)",
      opacity: 0.7,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
}));

const ThankYouImg = styled("img")(({ theme }) => ({
  width: "100px",
  height: "auto",
  margin: "0 auto",
  display: "block",
  [theme.breakpoints.down("sm")]: {
    width: "80px",
  },
}));

const OrderDetailsBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: "8px",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const CloseButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
  color: "#fff",
  padding: theme.spacing(1, 3),
  borderRadius: "8px",
  "&:hover": {
    background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
  },
}));

const ThankYouModal = ({
  show,
  onHide,
  totalPrice,
  state,
  orderId,
  orderDetails,
}) => {
  const { donationFrom } = useSelector((state) => state.gurdwaraDonation);
  const navigate = useNavigate();

  const onClose = () => {
    onHide();
    if (state?.hire) {
      navigate("/dashboardpage/orders");
    } else if (state?.item) {
      navigate(`/dashboardpage/my-items?tab=Purchased`);
    } else if (state?.donation && donationFrom !== "GURDWARA_NEED") {
      navigate("/dashboardpage/gurdwaramanagement");
    } else {
      navigate("/dashboardpage/inbox");
    }
    window.scroll(0, 0);
  };

  return (
    <StyledDialog
      open={show}
      onClose={onHide}
      TransitionComponent={Fade}
      transitionDuration={500}
      aria-labelledby="thank-you-dialog-title"
    >
      <DialogContent>
        <IconContainer>
          <CheckCircleIcon className="success-icon" />
        </IconContainer>

        {!state?.chatPay && !state?.donation && (
          <ThankYouImg src={ThankYouImage} alt="Thank You" />
        )}

        <Typography
          variant="h5"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold", color: "#1976d2", mt: 2 }}
        >
          {totalPrice !== 20 &&
          (state?.chatPay ||
            state?.invoicePay ||
            (state?.donation && donationFrom !== "GurdwaraNeed"))
            ? "PAYMENT COMPLETED SUCCESSFULLY!"
            : "Thank You for Your Order!"}
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: 2 }}>
          {totalPrice === 20 ? (
            "The customer phone number has been made available within the inbox section."
          ) : (
            <>
              {state?.chatPay ||
              state?.invoicePay ||
              (state?.donation && donationFrom !== "GurdwaraNeed")
                ? "Your payment has been processed successfully."
                : "Your booking is finalized once the vendor approves your transaction. Vendor has the right to decline the order request. You will only be charged upon order approval."}
            </>
          )}
        </Typography>

        {(orderId || orderDetails) && (
          <OrderDetailsBox>
            {orderId && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Order ID:</strong> {orderId}
              </Typography>
            )}
            {orderDetails && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Order Details:</strong>
                </Typography>
                <Typography variant="body2">
                  {typeof orderDetails === "object"
                    ? JSON.stringify(orderDetails, null, 2)
                    : orderDetails}
                </Typography>
              </>
            )}
          </OrderDetailsBox>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <CloseButton onClick={onClose}>Close</CloseButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ThankYouModal;
